import { createApi } from "@reduxjs/toolkit/query/react";
import { QuestionListResponseProps } from "@src/components/task-room-details/data.types";
import { QuizQuestionResponse } from "@src/pages/quiz/create-quiz-drawer/type&Validation";
import { baseQueryWithReauth } from "@src/utils/baseQuery";
import { QuestionTableData } from "@src/pages/question/question-table-component/QuestionTypes";
import { APIResponse, PaginationType } from "@src/types/CommonTypes";

export interface GetAllQuestionApiResponse extends APIResponse {
  response: PaginationType & { content: QuestionTableData[] };
}

export interface GetQuestionCountApiResponse extends APIResponse {
  response: {
    approvedQuestions: number;
    globalQuestions: number;
    pendingQuestions: number;
    rejectedQuestions: number;
    totalQuestions: number;
    [key: string]: number;
  };
}

export interface SubjectQuestionBulkUploadResponse extends APIResponse {
  response: string;
}

export interface McqQuestionBulkUploadResponse extends APIResponse {
  response: string;
}

export const QuestionApiSlice = createApi({
  reducerPath: "questionApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["getQuestionCount", "AudioFiles"],
  endpoints: (builder) => ({
    getAllQuestion: builder.query<
      GetAllQuestionApiResponse,
      {
        parentId: string;
        pageNo: number;
        pageSize: number;
        organizationId?: string;
      }
    >({
      query: ({ parentId, pageNo, pageSize, organizationId }) => {
        const params = new URLSearchParams({
          parentId,
          pageNo: String(pageNo),
          pageSize: String(pageSize),
        });

        if (organizationId) {
          params.set("organizationId", organizationId);
        }

        return {
          url: `question/questions-for-user?${params.toString()}`,
          method: "GET",
        };
      },
    }),

    addQuestion: builder.mutation<any, { payload: any }>({
      query: ({ payload }) => ({
        url: `question`,
        method: "POST",
        body: payload,
      }),
    }),
    updateQuestion: builder.mutation<any, { payload: any; id: string }>({
      query: ({ payload, id }) => ({
        url: `question?id=${id}`,
        method: "PUT",
        body: payload,
      }),
    }),
    getQuestionSummary: builder.query<any, { questionId: string }>({
      query: ({ questionId }) => ({
        url: `question/${questionId}`,
        method: "GET",
      }),
    }),
    getSharedQuestions: builder.query<
      any,
      {
        subjectId: string;
        questionType: string;
        pageNo: number;
        pageSize: number;
      }
    >({
      query: ({ subjectId, questionType, pageNo, pageSize }) => ({
        url: `question/shared-questions?subjectId=${subjectId}&questionType=${questionType}&pageNo=${pageNo}&pageSize=${pageSize}`,
        method: "GET",
      }),
    }),
    checkDuplicateQuestion: builder.query<
      any,
      { questionTitle: string; parentId: string }
    >({
      query: ({ questionTitle, parentId }) => ({
        url: `question/check-duplicate?questionTitle=${encodeURIComponent(
          questionTitle,
        )}&parentId=${parentId}`,
        method: "GET",
      }),
    }),
    acceptSharedQuestion: builder.mutation<
      any,
      { questionId: string; subjectId: string }
    >({
      query: ({ questionId, subjectId }) => ({
        url: `question/accept?questionId=${questionId}&subjectId=${subjectId}`,
        method: "PATCH",
      }),
    }),
    changeQuestionParent: builder.mutation<
      APIResponse,
      { questionId: string; parentId: string; parentType: string }
    >({
      query: ({ questionId, parentId, parentType }) => ({
        url: `question/change-parent?questionId=${encodeURIComponent(
          questionId,
        )}&parentId=${encodeURIComponent(parentId)}&parentType=${encodeURIComponent(
          parentType,
        )}`,
        method: "PATCH",
      }),
    }),
    getQuestionforMultipleIds: builder.query<
      any,
      {
        parentIds: string[];
        organizationId: string;
        pageNo: number;
        pageSize: number;
      }
    >({
      query: ({ parentIds, pageNo, pageSize, organizationId }) => ({
        url: `question/for-parents?pageNo=${pageNo}&pageSize=${pageSize}&organizationId=${organizationId}`,
        method: "Post",
        body: parentIds,
      }),
    }),
    getAssignedQuestionforBatch: builder.query<
      {
        statusCode: number;
        httpStatus: string;
        response: QuestionListResponseProps;
      },
      {
        batchId: string;
        parentId?: string;
        parentType?: string;
        pageNo: number;
        pageSize: number;
      }
    >({
      query: ({ batchId, parentId, parentType, pageNo, pageSize }) => ({
        url: `question/assigned-questions-for-batch?batchId=${batchId}&parentId=${parentId}&parentType=${parentType}&pageNo=${pageNo}&pageSize=${pageSize}`,
        method: "GET",
      }),
    }),
    getGenericQuestions: builder.query<
      {
        statusCode: number;
        httpStatus: string;
        response: QuestionListResponseProps;
      },
      {
        parentId: string;
        pageNo: number;
        pageSize: number;
      }
    >({
      query: ({ parentId, pageNo, pageSize }) => ({
        url: `question/get-generic-questions?parentId=${parentId}&pageNo=${pageNo}&pageSize=${pageSize}`,
        method: "GET",
      }),
    }),
    getAllUnassignedQuestions: builder.query<
      {
        statusCode: number;
        httpStatus: string;
        response: { content: QuizQuestionResponse[] } & PaginationType;
      },
      {
        batchId: string;
        parentId?: string;
        pageNo: number;
        pageSize: number;
      }
    >({
      query: ({ batchId, parentId, pageNo, pageSize }) => ({
        url: `question/to-assign-for-batch?batchId=${batchId}&parentId=${parentId}&pageNo=${pageNo}&pageSize=${pageSize}&`,
        method: "GET",
      }),
    }),
    assignQuestionsToBatch: builder.mutation<
      any,
      {
        trainerId: string;
        parentId?: string;
        parentType?: string;
        batchId: string;
        questions: string[];
      }
    >({
      query: ({ trainerId, batchId, parentId, parentType, questions }) => ({
        url: `question/assign?trainerId=${trainerId}&parentId=${parentId}&parentType=${parentType}&batchId=${batchId}&`,
        method: "POST",
        body: questions,
      }),
    }),
    getElpReport: builder.query<
      {
        statusCode: number;
        httpStatus: string;
        response: any;
      },
      {
        batchId: string;
        parentId?: string;
        studentId: string;
        parentType?: string;
      }
    >({
      query: ({ batchId, parentId, studentId, parentType }) => ({
        url: `question/get-elp-report?batchId=${batchId}&parentId=${parentId}&studentId=${studentId}&parentType=${parentType}`,
        method: "GET",
      }),
    }),
    getQuestionCount: builder.query<
      GetQuestionCountApiResponse,
      { subjectId: string; organizationId: string }
    >({
      query: ({ subjectId, organizationId }) => {
        let URL = `question/get-question-count?subjectId=${subjectId}`;
        if (organizationId) {
          URL += `&organizationId=${organizationId}`;
        }
        return {
          url: URL,
          method: "GET",
        };
      },
      providesTags: ["getQuestionCount"],
    }),
    bulkUploadMcqQuestionsV2: builder.mutation<
      McqQuestionBulkUploadResponse,
      { file: FormData }
    >({
      query: ({ file }) => {
        const URL = "question/bulk-upload";
        return {
          url: URL,
          method: "POST",
          body: file,
        };
      },
    }),
    bulkUploadXlsWithAdderRunV2: builder.mutation<
      any,
      { file: FormData }
    >({
      query: ({ file }) => {
        const URL = "question/bulkUploadXlsWithAdderRunV2";
        return {
          url: URL,
          method: "POST",
          body: file,
        };
      },
    }),
    saveAudioFile: builder.mutation<any, FormData>({
      query: (formData) => ({
        url: `question/save-audioFile`,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["AudioFiles"],
    }),
    getAudioFiles: builder.query<any, void>({
      query: () => ({
        url: `question/get-audioFiles`,
        method: "GET",
      }),
      providesTags: ["AudioFiles"],
    }),
    deleteAudioFile: builder.mutation<any, string>({
      query: (fileId) => ({
        url: `question/delete-audioFile?fileId=${fileId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["AudioFiles"],
    }),
    getBulkUploadXlsWithAdderRunV2Template: builder.query<Blob, void>({
      query: () => ({
        url: "question/bulkUploadXlsWithAdderRunV2/template",
        method: "GET",
        responseHandler: async (response) => {
          const blob = await response.blob();
          return blob;
        },
      }),
    }),
    getBulkUploadMcqQuestionsTemplate: builder.query<Blob, string | void>({
      query: (questionType) => ({
        // questionType is optional — without it the API returns the generic template.
        url: questionType
          ? `question/bulk-upload-template?questionType=${questionType}`
          : `question/bulk-upload-template`,
        method: "GET",
        responseHandler: async (response) => {
          const blob = await response.blob();
          return blob;
        },
      }),
    }),
    getStudentAudio: builder.query<Blob, { filePath: string }>({
      query: ({ filePath }) => ({
        url: `question/getStudentAudio`,
        method: "GET",
        params: { filePath },
        timeout: 20000,
        responseHandler: async (response) => {
          const blob = await response.blob();
          return blob;
        },
      }),
    }),
  }),
});

export const {
  useGetAllQuestionQuery,
  useAcceptSharedQuestionMutation,
  useChangeQuestionParentMutation,
  useAddQuestionMutation,
  useGetQuestionSummaryQuery,
  useUpdateQuestionMutation,
  useGetSharedQuestionsQuery,
  useGetQuestionforMultipleIdsQuery,
  useLazyGetQuestionforMultipleIdsQuery,
  useGetAssignedQuestionforBatchQuery,
  useLazyGetAssignedQuestionforBatchQuery,
  useGetGenericQuestionsQuery,
  useLazyGetGenericQuestionsQuery,
  useGetAllUnassignedQuestionsQuery,
  useLazyGetAllUnassignedQuestionsQuery,
  useAssignQuestionsToBatchMutation,
  useLazyGetElpReportQuery,
  useGetQuestionCountQuery,
  useLazyCheckDuplicateQuestionQuery,
  useLazyGetSharedQuestionsQuery,
  useLazyGetAllQuestionQuery,
  useBulkUploadMcqQuestionsV2Mutation,
  useBulkUploadXlsWithAdderRunV2Mutation,
  useLazyGetBulkUploadXlsWithAdderRunV2TemplateQuery,
  useLazyGetBulkUploadMcqQuestionsTemplateQuery,
  useSaveAudioFileMutation,
  useGetAudioFilesQuery,
  useLazyGetAudioFilesQuery,
  useDeleteAudioFileMutation,
  useGetStudentAudioQuery,
  useLazyGetStudentAudioQuery,
} = QuestionApiSlice;
